// Components/Pages/Projects.jsx
import { useState, useEffect } from "react";
import { Link } from "react-router-dom";

const Projects = () => {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      const response = await fetch('https://api.rigokenya.org/api/projects');
      if (!response.ok) {
        throw new Error('Failed to fetch projects');
      }
      const data = await response.json();
      setProjects(data);
    } catch (error) {
      console.error('Error fetching projects:', error);
      setError('Failed to load projects. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  // Get tag color based on tag name
  const getTagColor = (tag) => {
    const tagColors = {
      'Climate Justice & Environment': 'bg-emerald-100 text-emerald-700',
      'Gender Equality & Inclusion': 'bg-pink-100 text-pink-700',
      'Economic Empowerment & Innovation': 'bg-blue-100 text-blue-700',
      'Health & Nutrition': 'bg-green-100 text-green-700',
    };
    return tagColors[tag] || 'bg-gray-100 text-gray-700';
  };

  if (loading) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-primaryGold mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading projects...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-gray-50 min-h-screen py-12">
        <div className="container mx-auto px-4 text-center">
          <div className="bg-red-50 border-l-4 border-red-500 p-4 max-w-md mx-auto">
            <p className="text-red-700">{error}</p>
          </div>
          <button 
            onClick={fetchProjects}
            className="mt-4 bg-primaryGold text-primaryBlue px-4 py-2 rounded-lg hover:bg-yellow-400 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-12">
      <div className="container mx-auto px-4">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl md:text-5xl font-bold text-primaryBlue mb-4">
            Our Programs
          </h1>
          <p className="text-gray-600 text-lg max-w-3xl mx-auto">
            Explore our transformative projects that are creating lasting impact in communities across Western Kenya.
          </p>
          <div className="w-24 h-1 bg-primaryGold mx-auto mt-4"></div>
        </div>

        {/* Projects List - Numbered Style */}
        {projects.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-gray-500 text-lg">No projects available yet.</p>
            <p className="text-gray-400 text-sm mt-2">Check back soon for updates.</p>
          </div>
        ) : (
          <div className="max-w-4xl mx-auto space-y-8">
            {projects.map((project, index) => (
              <div 
                key={project.id}
                className="bg-white rounded-2xl shadow-lg hover:shadow-xl transition-shadow duration-300 overflow-hidden border border-gray-100"
              >
                <div className="p-6 md:p-8">
                  {/* Number and Title */}
                  <div className="flex items-start gap-4">
                    <div className="flex-shrink-0">
                      <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-primaryGold text-primaryBlue font-bold text-lg">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center gap-3 mb-2">
                        <h2 className="text-xl md:text-2xl font-bold text-primaryBlue">
                          {project.title}
                        </h2>
                        <span className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${getTagColor(project.tag)}`}>
                          {project.tag}
                        </span>
                      </div>
                      
                      {/* Donor Info */}
                      {project.donor && (
                        <p className="text-sm text-gray-500 mb-3">
                          <span className="font-medium">Funded by:</span> {project.donor}
                        </p>
                      )}

                      {/* Description */}
                      <p className="text-gray-700 leading-relaxed mb-3">
                        {project.description}
                      </p>

                      {/* Impact (if available) */}
                      {project.impact && (
                        <div className="mt-3 bg-gray-50 rounded-lg p-4 border-l-4 border-primaryGold">
                          <p className="text-sm text-gray-700">
                            <span className="font-semibold text-primaryBlue">Impact:</span> {project.impact}
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Call to Action */}
        <div className="mt-16 text-center">
          <Link
            to="/donate"
            className="inline-block bg-primaryGold text-primaryBlue px-8 py-4 rounded-lg font-semibold hover:bg-yellow-400 transition-all duration-300 shadow-md hover:shadow-lg transform hover:-translate-y-0.5"
          >
            Support Our Programs
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Projects;